import { ANTHROPIC_INTEGRATION_ID } from "@bloomstack/apis/integrations/anthropic/anthropicTypes";
import { OPENAI_INTEGRATION_ID } from "@bloomstack/apis/integrations/openai/openAITypes";
import { GOOGLE_INTEGRATION_ID } from "@bloomstack/apis/integrations/google/googleTypes";
import { PERPLEXITY_INTEGRATION_ID } from "@bloomstack/apis/integrations/perplexity/perplexityTypes";
import { MOONSHOT_DEFAULT_BASE_URL, MOONSHOT_INTEGRATION_ID } from "@bloomstack/apis/integrations/moonshot/moonshotTypes";
import { HUGGINGFACE_DEFAULT_BASE_URL, HUGGINGFACE_INTEGRATION_ID } from "@bloomstack/apis/integrations/huggingface/huggingfaceTypes";
import { DEEPSEEK_DEFAULT_BASE_URL, DEEPSEEK_INTEGRATION_ID } from "@bloomstack/apis/integrations/deepseek/deepseekTypes";
import { XAI_DEFAULT_BASE_URL, XAI_INTEGRATION_ID } from "@bloomstack/apis/integrations/xai/xaiTypes";
import { DEEPGRAM_INTEGRATION_ID } from "@bloomstack/apis/integrations/deepgram/deepgramTypes";
import { X_INTEGRATION_ID } from "@bloomstack/apis/integrations/x/xTypes";
import { MCP_INTEGRATION_ID } from "@bloomstack/apis/integrations/mcp/mcpTypes";
import { GH_INTEGRATION_ID } from "@bloomstack/apis/integrations/github/githubTypes";
import { Integration, IntegrationInstance } from "@bloomstack/apis/integrations/instanceTypes";
import React, { createContext, lazy, ReactNode, Suspense, useContext } from "react";
import Dynamic from '@/lib/dynamic'
import ComponentSpinner from "@/components/ui/componentSpinner";

const XIntegrationForm = Dynamic(() => import("../components/integrations/x/xIntegrationForm").then(m => ({ default: m.XIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const AnthropicIntegrationForm = Dynamic(() => import("../components/integrations/anthropic/anthropicIntegrationForm").then(m => ({ default: m.AnthropicIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const OpenAIIntegrationForm = Dynamic(() => import("../components/integrations/openai/openAIIntegrationForm").then(m => ({ default: m.OpenAIIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const GoogleIntegrationForm = Dynamic(() => import("../components/integrations/google/googleIntegrationForm").then(m => ({ default: m.GoogleIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const PerplexityIntegrationForm = Dynamic(() => import("../components/integrations/perplexity/perplexityIntegrationForm").then(m => ({ default: m.PerplexityIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const MoonshotIntegrationForm = Dynamic(() => import("../components/integrations/moonshot/moonshotIntegrationForm").then(m => ({ default: m.MoonshotIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const HuggingFaceIntegrationForm = Dynamic(() => import("../components/integrations/huggingface/huggingfaceIntegrationForm").then(m => ({ default: m.HuggingFaceIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const DeepSeekIntegrationForm = Dynamic(() => import("../components/integrations/deepseek/deepseekIntegrationForm").then(m => ({ default: m.DeepSeekIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const XaiIntegrationForm = Dynamic(() => import("../components/integrations/xai/xaiIntegrationForm").then(m => ({ default: m.XaiIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const DeepgramIntegrationForm = Dynamic(() => import("../components/integrations/deepgram/deepgramIntegrationForm").then(m => ({ default: m.DeepgramIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const McpIntegrationForm = Dynamic(() => import("../components/integrations/mcp/mcpIntegrationForm").then(m => ({ default: m.McpIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });
const GithubIntegrationForm = Dynamic(() => import("../components/integrations/github/githubIntegrationForm").then(m => ({ default: m.GithubIntegrationForm })), { ssr: false, loading: () => <ComponentSpinner /> });

type IntegrationFormFactory = {
  edit: (
    instance: IntegrationInstance,
    onValid: (instance: IntegrationInstance) => void,
    onInvalid: () => void
  ) => React.ReactElement;
  create: (
    integration: Integration,
    onValid: (instance: IntegrationInstance) => void,
    onInvalid: () => void,
    onRequestSave?: () => Promise<IntegrationInstance | undefined>,
    initialData?: Record<string, any>
  ) => React.ReactElement;
}

const INTEGRATION_FACTORY: Record<string, IntegrationFormFactory> = {
  [X_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<XIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<XIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "default",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "", // Assuming userId will be set later
        data: {
          apiKey: "",
          apiSecret: ""
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [ANTHROPIC_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<AnthropicIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<AnthropicIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "", // Assuming userId will be set later
        data: {
          apiKey: "",
          baseUrl: ""
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [OPENAI_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<OpenAIIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<OpenAIIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "", // Assuming userId will be set later
        data: {
          apiKey: "",
          baseUrl: ""
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [GOOGLE_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<GoogleIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<GoogleIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "", // Assuming userId will be set later
        data: {
          apiKey: ""
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [PERPLEXITY_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<PerplexityIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<PerplexityIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "",
        data: {
          apiKey: ""
        }
      } as IntegrationInstance}
      newIntegration
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [MOONSHOT_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<MoonshotIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<MoonshotIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "",
        data: {
          apiKey: "",
          baseUrl: MOONSHOT_DEFAULT_BASE_URL,
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [DEEPSEEK_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<DeepSeekIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<DeepSeekIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "",
        data: {
          apiKey: "",
          baseUrl: DEEPSEEK_DEFAULT_BASE_URL,
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [XAI_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<XaiIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<XaiIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "",
        data: {
          apiKey: "",
          baseUrl: XAI_DEFAULT_BASE_URL,
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [HUGGINGFACE_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<HuggingFaceIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<HuggingFaceIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "",
        data: {
          apiKey: "",
          baseUrl: HUGGINGFACE_DEFAULT_BASE_URL,
          billTo: "",
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [DEEPGRAM_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<DeepgramIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<DeepgramIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "", // Assuming userId will be set later
        data: {
          apiKey: ""
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [GH_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<GithubIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid) => (<GithubIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "default",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "",
        data: {
          accessToken: ""
        }
      } as IntegrationInstance}
      newIntegration
      onValid={onValid}
      onInvalid={onInvalid}
    />)
  },
  [MCP_INTEGRATION_ID]: {
    edit: (instance, onValid, onInvalid) => (<McpIntegrationForm
      instance={instance}
      onValid={onValid}
      onInvalid={onInvalid}
    />),
    create: (integration, onValid, onInvalid, onRequestSave, initialData) => (<McpIntegrationForm
      instance={{
        id: integration.id,
        integrationId: integration.id,
        label: integration.label,
        alias: "default",
        description: integration.description,
        icon: integration.icon,
        enabled: true,
        userId: "", // Assuming userId will be set later
        data: {
          serverUrl: "",
          apiKey: "",
          namespace: "",
          ...initialData,
        }
      } as IntegrationInstance}
      onValid={onValid}
      onInvalid={onInvalid}
      onRequestSave={onRequestSave}
    />)
  }
};

/**
 * Context for managing integration forms and related operations.
 */
export type IntegrationsContextApi = {
  /**
   * Get the integration form component for the given integration instance.
   * @param instance The integration instance to get the form for.
   * @param onValid Callback function to call when the form is valid.
   * @param onInvalid Callback function to call when the form is invalid.
   * @returns The integration form component for the given instance.
   */
  getIntegrationFormComponent: (
    instance: IntegrationInstance,
    onValid: (instance: IntegrationInstance) => void,
    onInvalid: () => void
  ) => React.ReactElement;

  /**
   * Get a new integration form component for the given integration.
   * @param integration The integration to get the form for.
   * @param onValid Callback function to call when the form is valid.
   * @param onInvalid Callback function to call when the form is invalid.
   * @param onRequestSave Optional callback to auto-save the instance (used by MCP OAuth).
   * @param initialData Optional initial data to pre-populate the form fields.
   * @returns The new integration form component for the given integration.
   */
  getNewIntegrationFormComponent: (
    integration: Integration,
    onValid: (instance: IntegrationInstance) => void,
    onInvalid: () => void,
    onRequestSave?: () => Promise<IntegrationInstance | undefined>,
    initialData?: Record<string, any>
  ) => React.ReactElement;
}

export const IntegrationsContext = createContext<IntegrationsContextApi>(undefined!);

/**
 * Hook to access the IntegrationsContext.
 * @returns The IntegrationsContext API.
 */
export const useIntegrationsContext = () => {
  const context = useContext(IntegrationsContext);
  if (!context) {
    throw new Error("useIntegrationsContext must be used within an IntegrationsProvider");
  }
  return context;
}

export type IntegrationsProviderProps = {
  children: ReactNode;
}

/**
 * Provider component for the IntegrationsContext.
 * @returns The provider component for the IntegrationsContext.
 */
export const IntegrationsProvider = ({ children }: IntegrationsProviderProps) => {
  const getIntegrationFormComponent = (
    instance: IntegrationInstance,
    onValid: (instance: IntegrationInstance) => void,
    onInvalid: () => void
  ): React.ReactElement => {
    if (Reflect.has(INTEGRATION_FACTORY, instance.integrationId)) {
      return INTEGRATION_FACTORY[instance.integrationId].edit(instance, onValid, onInvalid);
    }

    throw new Error(`No form component found for integration: ${instance.integrationId}`);
  };

  const getNewIntegrationFormComponent = (
    integration: Integration,
    onValid: (instance: IntegrationInstance) => void,
    onInvalid: () => void,
    onRequestSave?: () => Promise<IntegrationInstance | undefined>,
    initialData?: Record<string, any>
  ): React.ReactElement => {
    if (Reflect.has(INTEGRATION_FACTORY, integration.id)) {
      return INTEGRATION_FACTORY[integration.id].create(integration, onValid, onInvalid, onRequestSave, initialData);
    }

    throw new Error(`No form component found for integration: ${integration.id}`);
  }

  const value: IntegrationsContextApi = {
    getIntegrationFormComponent,
    getNewIntegrationFormComponent
  };

  return (
    <IntegrationsContext.Provider value={value}>
      {children}
    </IntegrationsContext.Provider>
  );
};
